import { css } from '@emotion/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { surfaceBrandColorAsTextColorStyle } from 'components/colors';
import { Option, Size } from 'components/Form/SelectCards/types';
import React from 'react';
import tw, { TwStyle } from 'twin.macro';

const SIZE_STYLES: Record<
  Size,
  {
    icon: TwStyle;
    container: TwStyle;
  }
> = {
  'extra-small': {
    icon: tw`size-4`,
    container: tw`h-5`,
  },
  small: {
    icon: tw`size-5`,
    container: tw`h-6`,
  },
  big: {
    icon: tw`size-5`,
    container: tw`h-6`,
  },
};

interface Props<T> {
  checked?: boolean;
  onChange: (checked: boolean) => void;
  size: Size;
  id: string;
  option: Option<T>;
  ariaLabel: string | undefined;
}

export function Checkbox<T>({
  checked,
  onChange,
  size,
  id,
  option,
  ariaLabel,
}: Props<T>) {
  const sizeStyles = SIZE_STYLES[size];
  const { description } = option;

  return (
    <div css={[tw`flex items-center relative`, sizeStyles.container]}>
      <input
        id={id}
        aria-describedby={description ? `${id}-description` : undefined}
        role="checkbox"
        type="checkbox"
        className="peer"
        tw="sr-only"
        checked={checked}
        onChange={() => onChange(!checked)}
        aria-label={ariaLabel}
      />
      <div
        css={[
          surfaceBrandColorAsTextColorStyle,
          tw`border border-neutral-moderate rounded-sm flex items-center justify-center cursor-pointer transition-shadow`,
          tw`peer-focus-visible:ring-2 peer-focus-visible:ring-brand-intense peer-focus-visible:ring-offset-2`,
          checked && tw`bg-brand-intense border-brand-intense`,
          sizeStyles.icon,
          css`
            background-image: none !important;
          `,
        ]}
        aria-hidden
      >
        {checked && <CheckIcon tw="text-white pointer-events-none" />}
      </div>
    </div>
  );
}
